import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
} from '../../components/Breadcrumb';
import { Button } from '../../components/Button';
import Content from '../../components/Content';
import Foldout, { type IFoldoutItem } from '../../components/Foldout';
import { H1, HeadingTail } from '../../components/Heading';
import { HStack } from '../../components/HStack';
import { IconDirLeft, IconDirRight, IconKebab, IconSupportRound } from '../../components/Icon';
import { LinkButton } from '../../components/LinkButton';
import Loader from '../../components/Loader';
import MediaPreview from '../../components/MediaPreview';
import Panel from '../../components/Panel';
import { Text } from '../../components/Text';
import { UtilityButton } from '../../components/UtilityButton';
import { SkeletonPageHeader } from '../SkeletonTemplate';
import {
  Meta,
  Navigation,
  PageAlerts,
  PageContent,
  PageContentText,
  PageUtility,
  Tabs,
  Wrapper,
} from './wrapper';

interface PageHeaderCtaProps {
  title: string;
  displayName: string;
  iconBefore?: React.ReactNode;
  iconAfter?: React.ReactNode;
  loading?: boolean;
  disabled?: boolean;
  onClick: () => void;
  testId?: string;
}

interface PageHeaderBreadcrumbsLinkProps {
  to: string;
  title: string;
}

export interface PageHeaderBreadcrumbsProps {
  displayName: string;
  link?: PageHeaderBreadcrumbsLinkProps;
}

interface PageHeaderImageProps {
  src: string;
  srcSet?: string;
  alt: string;
}

interface PageHeaderProps {
  heading: string;
  headingTail?: string;
  preamble?: React.ReactNode;
  image?: PageHeaderImageProps;
  cta?: PageHeaderCtaProps;
  customCta?: React.ReactNode; // Note: Should only be used in specific cases where cta is not applicable
  breadcrumbs?: PageHeaderBreadcrumbsProps[];
  alerts?: React.ReactNode[];
  status?: React.ReactNode;
  tabs?: React.ReactNode;
  foldout?: IFoldoutItem[][];
  loading?: boolean;
  guide?: React.ReactNode;
  content?: React.ReactNode;
}

const PageHeader = ({
  heading,
  headingTail,
  preamble,
  image,
  cta,
  customCta,
  breadcrumbs,
  alerts,
  status,
  tabs,
  foldout,
  loading,
  guide,
  content,
}: PageHeaderProps) => {
  const tCommon = useTranslation('common').t;

  const [utilityMenu, setUtilityMenu] = useState(false);
  const [guidePanel, setGuidePanel] = useState(false);
  const onToggleUtilityMenu = () => setUtilityMenu(!utilityMenu);

  const onOpenGuidePanel = () => setGuidePanel(true);
  const onCloseGuidePanel = () => setGuidePanel(false);

  if (loading) {
    return <SkeletonPageHeader />;
  }

  return (
    <>
      {guide && guidePanel && (
        <Panel title={tCommon('guide')} onClose={onCloseGuidePanel}>
          {guide}
        </Panel>
      )}
      <Wrapper>
        {alerts && alerts.length > 0 && <PageAlerts>{alerts.map((alert) => alert)}</PageAlerts>}
        {breadcrumbs && (
          <Navigation>
            <Breadcrumb>
              <BreadcrumbList>
                {breadcrumbs.map((breadcrumb, i) => (
                  <BreadcrumbItem key={`${breadcrumb.displayName}-${i}`}>
                    {breadcrumb.link ? (
                      <BreadcrumbLink to={breadcrumb.link.to} title={breadcrumb.link.title}>
                        {breadcrumb.displayName}
                      </BreadcrumbLink>
                    ) : (
                      breadcrumb.displayName
                    )}
                  </BreadcrumbItem>
                ))}
              </BreadcrumbList>
            </Breadcrumb>

            <PageUtility>
              {false && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Text $tone="alt" $size="small">
                    {tCommon('currentOfMaxCount', {
                      current: 1,
                      max: 43,
                    })}
                  </Text>
                  <UtilityButton onClick={() => {}} title="" disabled>
                    <IconDirLeft />
                  </UtilityButton>
                  <UtilityButton onClick={() => {}} title="">
                    <IconDirRight />
                  </UtilityButton>
                </div>
              )}
              {!!foldout?.length && (
                <Foldout
                  isOpen={utilityMenu}
                  onClose={onToggleUtilityMenu}
                  posX="end"
                  items={foldout}
                >
                  <UtilityButton onClick={onToggleUtilityMenu} title="">
                    <IconKebab />
                  </UtilityButton>
                </Foldout>
              )}
            </PageUtility>
          </Navigation>
        )}
        <PageContent>
          <PageContentText>
            {status}
            {image ? (
              <HStack $spacing={2} $valign="center">
                <MediaPreview image={image} />
                <H1 $margin="none">
                  {heading}
                  {headingTail && (
                    <>
                      {' '}
                      <HeadingTail>{headingTail}</HeadingTail>
                    </>
                  )}
                </H1>
              </HStack>
            ) : (
              <H1 $margin="none">
                {heading}
                {headingTail && (
                  <>
                    {' '}
                    <HeadingTail>{headingTail}</HeadingTail>
                  </>
                )}
              </H1>
            )}
            {preamble && (
              <Content textDarkLighten small>
                {preamble}
              </Content>
            )}
            {guide && (
              <LinkButton
                $size="small"
                onClick={onOpenGuidePanel}
                title={tCommon('guide')}
                $maxContent
              >
                <IconSupportRound />
                {tCommon('guide')}
              </LinkButton>
            )}
          </PageContentText>
          {cta ? (
            <Button
              type="button"
              $tone="primary"
              onClick={cta.onClick}
              title={cta.title}
              disabled={cta.disabled}
              data-testid={cta.testId}
            >
              {cta.iconBefore}
              {cta.displayName}
              {cta.iconAfter}
              {cta.loading && <Loader inlineBlock />}
            </Button>
          ) : customCta ? (
            customCta
          ) : undefined}
        </PageContent>
        {content && <Meta>{content}</Meta>}
        {tabs && <Tabs>{tabs}</Tabs>}
      </Wrapper>
    </>
  );
};

export default PageHeader;
